import React, { useState, useRef, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { usePromiseTracker } from 'react-promise-tracker'
import {
  Grid, FormControl, InputLabel,
  Select, Button, TextField, MenuItem,
  Typography, Card, InputAdornment,
  FilledInput,
  CardMedia, CardContent, CardActions, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress
} from '@material-ui/core'

// import Image from '@material-ui/icons/CropOriginal'
import Cancel from '@material-ui/icons/Cancel'

import { makeStyles } from '@material-ui/core/styles'
import PublishIcon from '@material-ui/icons/Publish'

import { ChromePicker } from 'react-color'
import States from '../states.json'
import TimeZones from '../timezones.json'

import { NotificationToast, BaseToast } from '../../tools/Toast'
import { updateOrgGeneralSettings, updateAccountImage, getOrgGeneralSettings, fetchAccountSupportKey } from '../../../../redux/actions'
import { handleTextPrepper, Toast } from '../../../../utils'
import { loadingAreas } from '../../../../utils/variables'

// import log from 'loglevel'

const useStyles = makeStyles((theme) => ({
  blueBtn: {
    backgroundColor: '#3984f7',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#0651C4'
    }
  },
  greenBtn: {
    color: '#fff',
    backgroundColor: '#A4D164',
    '&:hover': {
      backgroundColor: '#8AB651'
    }
  },
  logo: {
    marginTop: '0em',
    width: '75%',
    height: 'auto',
    color: '#a7a7a7'
  },
  colorCard: {
    padding: '1rem'
  },
  uploadInput: {
    display: 'none'
  },
  imageNameAvatar: {
    background: theme.palette.background.paper,
    '&:hover': {
      background: theme.palette.background.default
    },
    '&.Mui-focused': {
      background: theme.palette.background.default
    }
  },
  listImg: {
    // paddingTop: '100%',
    width: '100%',
    margin: 'auto 0',
    backgroundSize: 'contain'
  }
}))

const useTextStyles = makeStyles((theme) => ({
  root: {
    overflow: 'hidden',
    backgroundColor: '#fff',
    width: '100%',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    },
    '&$focused': {
      backgroundColor: '#f5f5f5 !important'
    }
  },
  focused: {}
}))

const useSelectStyles = makeStyles((theme) => ({
  select: {
    overflow: 'hidden',
    backgroundColor: '#fff',
    width: '100%',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    transition: 'background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
    '&:focus': {
      backgroundColor: '#f5f5f5'
    },
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  }
}))

/* React color picker https://casesandberg.github.io/react-color/ --- sets selected color value to state --- slightly changed to use hooks */

const defaultForm = {
  accountName: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  postalCode: '',
  timeZone: '',
  primaryColor: '',
  secondaryColor: '',
  imageKey: '',
  imagePath: ''
}

/**
 * Returns organization general forms
 */
const OrganizationGeneral = () => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const textClasses = useTextStyles()
  const selectClasses = useSelectStyles()
  const { promiseInProgress } = usePromiseTracker({ area: loadingAreas.FETCH_SUPPORT_KEY })

  const fileInput = useRef()

  const settings = useSelector(state => state.orgSettings.general) || {}
  const [form, setForm] = useState({ ...defaultForm })
  const [accountSupportKey, setAccountSupportKey] = useState('')
  const [supportKeyOpen, setSupportKeyOpen] = useState(false)

  // will be only evaluated if the settings object changes
  useMemo(() => {
    if (settings && settings.accountName) {
      setForm({
        accountName: settings.accountName,
        address: settings.address,
        address2: settings.address2,
        city: settings.city,
        state: settings.state,
        postalCode: settings.postalCode,
        timeZone: settings.timeZone,
        primaryColor: settings.primaryColor || '',
        secondaryColor: settings.secondaryColor || '',
        imageKey: settings.imageKey || '',
        imagePath: settings.imagePath || ''
      })
    }
  }, [settings])

  // console.log(JSON.stringify(settings, null, 2))

  const onChange = (prop) => e => {
    setForm({ ...form, [prop]: e.target.value })
  }

  const [colorPickerDisplayPrimary, setColorPickerDisplayPrimary] = useState(false)
  const [colorPickerDisplaySecondary, setColorPickerDisplaySecondary] = useState(false)

  const [tempImage, setTempImage] = useState('')
  const [imageName, setImageName] = useState('')
  const [showImageInput, setShowImageInput] = useState(false)

  const submitForm = async () => {
    const settings = Object.assign({}, form)
    settings.accountName = handleTextPrepper(settings.accountName)
    settings.address = handleTextPrepper(settings.address)
    settings.address2 = handleTextPrepper(settings.address2)
    settings.city = handleTextPrepper(settings.city)
    settings.state = handleTextPrepper(settings.state)
    settings.postalCode = handleTextPrepper(settings.postalCode)
    settings.timeZone = handleTextPrepper(settings.timeZone)
    settings.primaryColor = handleTextPrepper(settings.primaryColor)
    settings.secondaryColor = handleTextPrepper(settings.secondaryColor)
    dispatch(updateOrgGeneralSettings(settings, fireUpdateSuccess, fireUpdateFailure))
  }

  const fireUpdateSuccess = () => NotificationToast(false, 'Settings updated successfully!')
  const fireUpdateFailure = () => NotificationToast(true, 'Failed to update settings!')

  const togglePrimaryColorPicker = (e) => {
    e.preventDefault()
    if (!colorPickerDisplayPrimary) {
      setColorPickerDisplayPrimary(true)
    } else {
      setColorPickerDisplayPrimary(false)
    }
  }

  const setPrimaryColor = (e) => {
    const newForm = { ...form }
    newForm.primaryColor = e.hex
    setForm(newForm)
    // log.debug(newForm)
  }

  const toggleSecondaryColorPicker = (e) => {
    e.preventDefault()
    if (!colorPickerDisplaySecondary) {
      setColorPickerDisplaySecondary(true)
    } else {
      setColorPickerDisplaySecondary(false)
    }
  }

  const setSecondaryColor = (e) => {
    const newForm = { ...form }
    newForm.secondaryColor = e.hex
    setForm(newForm)
  }

  const getStateOptions = () => {
    const items = []
    for (const [key, value] of Object.entries(States)) {
      items.push(<MenuItem id={key} key={key} value={key}>{value}</MenuItem>)
    }
    return items
  }

  const getTimeZoneOptions = () => {
    const items = []
    for (const [key, value] of Object.entries(TimeZones)) {
      items.push(<MenuItem id={key} key={key} value={key}>{value}</MenuItem>)
    }
    return items
  }

  const popover = {
    position: 'absolute',
    zIndex: '2'
  }
  const cover = {
    position: 'fixed',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px'
  }

  const handleImageChange = (e) => {
    e.preventDefault()
    const file = e.target.files[0]
    // 2mb file size 2097152
    if (file.size > 2097152) {
      Toast.fire({
        icon: 'info',
        title: 'The selected file is too large.'
      })
    } else {
      setImageName(file.name)
      const reader = new window.FileReader()
      reader.readAsDataURL(file)
      reader.onloadend = () => {
        setTempImage(reader.result)
      }
    }
  }

  const submitAvatarUpdate = async () => {
    const image = tempImage
    dispatch(updateAccountImage(image, fireImageUpdateSuccess, fireImageUpdateFailure))
  }

  const fireImageUpdateSuccess = () => {
    BaseToast('success', 'Account image updated successfully! Changes will be reflected shortly.')
    setTempImage('')
    setImageName('')
    setShowImageInput(!showImageInput)
    setTimeout(() => {
      dispatch(getOrgGeneralSettings())
    }, 20000)
  }

  const fireImageUpdateFailure = () => {
    // NotificationToast(true, 'Failed to update account image.')
    setTempImage('')
    setImageName('')
    setShowImageInput(!showImageInput)
    BaseToast('error', 'Failed to update account image.')
  }

  const imageInput = () => {
    if (showImageInput === true) {
      return (
        <>
          <Grid item container direction='row' justify='center'>
            <Grid item>
              <Typography variant='caption' component='p' className='text-muted' align='center'>
                Upload limit: 2mb
              </Typography>
            </Grid>
          </Grid>

          <Grid item container direction='row' justify='center' style={{ marginBottom: '10px' }}>
            <Grid item>
              <Typography variant='caption' component='p' className='text-muted' align='center'>
                Image Ratio: 500 x 500
              </Typography>
            </Grid>
          </Grid>

          <Grid item container direction='row' justify='center'>
            <Grid item xs={10}>
              <input
                accept='image/*'
                className={classes.uploadInput}
                id='account-upload'
                multiple
                type='file'
                ref={fileInput}
                onChange={(e) => handleImageChange(e)}
              />
              <label htmlFor='account-upload' className='w-100'>
                {/* <Grid container direction='row' alignItems='center' justify='space-evenly'>
                  <Grid item xs={8}>
                    <Avatar variant='rounded' className={classes.imageNameAvatar}>{imageName}</Avatar>
                  </Grid>
                  <Grid item xs={4}>
                    <Button variant='contained' color='primary' component='span' className={classes.button}>
                      Upload
                    </Button>
                  </Grid>
                </Grid> */}
                <FilledInput
                  value={imageName}
                  className={classes.imageNameAvatar}
                  fullWidth
                  readOnly
                  endAdornment={
                    <InputAdornment position='end'>
                      <Button variant='text' color='primary' component='span' size='small'>
                        <PublishIcon />
                      </Button>
                    </InputAdornment>
                  }
                />
              </label>
            </Grid>
          </Grid>

          <Grid item container direction='row' justify='center'>
            <Button
              variant='contained'
              className={classes.greenBtn}
              style={{ marginRight: '20px' }}
              onClick={(e) => { e.preventDefault(); setShowImageInput(!showImageInput); setTempImage(''); setImageName('') }}
            >
              <Cancel />
            </Button>
            <Button
              variant='contained'
              className={classes.blueBtn}
              onClick={(e) => { e.preventDefault(); submitAvatarUpdate() }}
            >
              Save
            </Button>
          </Grid>
        </>
      )
    } else {
      return (
        <>
          <Grid container item direction='row' justify='center'>
            <Button
              variant='contained'
              className={classes.greenBtn}
              onClick={(e) => { e.preventDefault(); setShowImageInput(!showImageInput); setTempImage(''); setImageName('') }}
            >
              Change Image
            </Button>
          </Grid>
        </>
      )
    }
  }

  const fireSupportKeySuccess = (key) => {
    setAccountSupportKey(key)
    setSupportKeyOpen(true)
  }

  const fetchSupportKey = () => {
    dispatch(fetchAccountSupportKey(fireSupportKeySuccess, () => NotificationToast(true, 'Unable to fetch Support Key.')))
  }

  const closeSupportKeyModal = () => {
    setSupportKeyOpen(false)
    setAccountSupportKey('')
  }

  const isEnabled = form.accountName !== settings.accountName || form.address !== settings.address || form.address2 !== settings.address2 ||
    form.city !== settings.city || form.state !== settings.state || form.postalCode !== settings.postalCode || form.timeZone !== settings.timeZone || form.logoKey !== settings.logoKey ||
    form.primaryColor !== settings.primaryColor || form.secondaryColor !== settings.secondaryColor

  return (
    <>
      <Grid container direction='column' justify='flex-start' spacing={2} className='mx-auto w-100 h-100'>

        <Grid container item direction='row' className='mx-auto w-100'>
          <Grid item container xs={8} justify='flex-start'>
            <h2>Account Info</h2>
          </Grid>

          <Grid item container xs={4} justify='center'>
            <Button
              className={classes.blueBtn}
              disabled={!isEnabled}
              variant='contained'
              size='large'
              onClick={() => submitForm('user.general', form)}
            >
              Save
            </Button>
          </Grid>
        </Grid>
        <Grid container item direction='row' className='mt-3 w-100 mx-auto px-0 mr-2' justify='space-evenly'>
          <Grid container item direction='column' xs={8} spacing={2}>
            <Grid container item direction='row'>
              <Grid item xs={12}>
                <TextField
                  label='Account Name'
                  required
                  name='orgName'
                  variant='filled'
                  error={form.accountName.length === 0}
                  value={form.accountName ? form.accountName : ''}
                  onChange={onChange('accountName')}
                  InputProps={{
                    classes: textClasses
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container item direction='row'>
              <Grid item xs={12}>
                <TextField
                  label='Address'
                  required
                  name='address'
                  variant='filled'
                  error={form.address.length === 0}
                  value={form.address ? form.address : ''}
                  onChange={onChange('address')}
                  InputProps={{
                    classes: textClasses
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container item direction='row'>
              <Grid item xs={12}>
                <TextField
                  label='Address 2'
                  required
                  name='address2'
                  variant='filled'
                  error={form.address2.length === 0}
                  value={form.address2 ? form.address2 : ''}
                  onChange={onChange('address2')}
                  InputProps={{
                    classes: textClasses
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container item direction='row' spacing={2}>
              <Grid item xs={5}>
                <TextField
                  label='City'
                  required
                  name='city'
                  variant='filled'
                  error={form.city.length === 0}
                  value={form.city ? form.city : ''}
                  onChange={onChange('city')}
                  InputProps={{
                    classes: textClasses
                  }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel htmlFor='state-label' variant='filled'>State</InputLabel>
                  <Select
                    value={form.state}
                    onChange={onChange('state')}
                    classes={selectClasses}
                    variant='filled'
                    labelId='state-label'
                  >
                    {getStateOptions()}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={3}>
                <TextField
                  label='Zip'
                  required
                  name='zip'
                  variant='filled'
                  error={form.postalCode.length === 0}
                  value={form.postalCode ? form.postalCode : ''}
                  onChange={onChange('postalCode')}
                  InputProps={{
                    classes: textClasses
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container item direction='row'>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel htmlFor='timezone-label' variant='filled'>Time Zone</InputLabel>
                  <Select
                    value={form.timeZone}
                    onChange={onChange('timeZone')}
                    classes={selectClasses}
                    variant='filled'
                    labelId='timezone-label'
                  >
                    {getTimeZoneOptions()}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

          </Grid>

          <Grid container item xs={4} direction='column' justify='flex-start' className='pr-0' spacing={2}>
            <Grid item container direction='row' className='w-100'>
              <Card style={{ width: '100%', padding: '.5rem' }}>
                {/* <CardActionArea> */}
                <CardMedia
                  style={{ height: 120 }}
                  className={classes.listImg}
                  image={tempImage.length > 0 ? tempImage : settings && settings.imagePath ? `${settings.imagePath}?${new Date().getTime()}` : ''}
                  title='School Logo'
                />
                {/* </CardActionArea> */}
                <CardContent style={{ padding: 5 }}>
                  <Typography variant='body1' align='center'>Account Image</Typography>
                </CardContent>
                <CardActions>
                  <Grid item container direction='column' justify='center'>
                    {imageInput()}
                  </Grid>
                </CardActions>
              </Card>
            </Grid>
            <Grid item container justify='center'>
              <Button variant='contained' className={classes.greenBtn} onClick={fetchSupportKey}>
                {promiseInProgress ? <CircularProgress size={20} color='#FFFFFF' /> : 'Get Support Key'}
              </Button>
            </Grid>

          </Grid>

          {/* <Grid container item direction='column' xs={4} spacing={2} alignItems='center'>
            <Grid item container direction='row' justify='center' className='p-0'>
              <Image className={`${classes.logo}`} />
            </Grid>
            <Grid item>
              <Button className={classes.greenBtn} variant='contained'>Change School Logo</Button>
            </Grid>
            <Grid item>
              <Typography variant='caption' className='text-muted'>Upload limit: 2mb</Typography>
            </Grid>

            <Grid item>
              <Typography variant='caption' className='text-muted'>Image Ratio 640 x 480</Typography>
            </Grid>
          </Grid> */}
        </Grid>

        <Grid container item direction='row'>
          <Grid container item direction='column' xs={12}>
            <Grid container item direction='row' className='mx-auto w-100 my-2'>
              <h5>Account Colors</h5>
            </Grid>

            <Grid container item direction='row' className='mx-auto w-100' spacing={2}>
              <Grid item container xs={6} direction='column' spacing={2}>
                <Card className={`${classes.colorCard} mr-2`}>
                  <Grid item container direction='row'>
                    <Grid item container direction='column' xs={7}>
                      <Typography>Primary Color</Typography>
                    </Grid>

                    <Grid item container direction='column' xs={5}>
                      <Button
                        variant='contained'
                        className={classes.greenBtn}
                        onClick={togglePrimaryColorPicker}
                      >
                        Pick Color
                      </Button>
                    </Grid>
                  </Grid>

                  <Grid item container direction='row' className='mt-4'>
                    {colorPickerDisplayPrimary &&
                      <div style={popover}>
                        <div style={cover} onClick={togglePrimaryColorPicker} />
                        <ChromePicker color={form.primaryColor} onChange={e => setPrimaryColor(e)} />
                      </div>}
                  </Grid>

                  <Grid item>
                    <div className='' style={{ backgroundColor: form.primaryColor, color: form.primaryColor, height: '6em' }}>{form.primaryColor}</div>
                  </Grid>
                </Card>
              </Grid>

              <Grid item container xs={6} direction='column' spacing={2}>
                <Card className={`${classes.colorCard} ml-2`}>
                  <Grid item container direction='row'>
                    <Grid item container direction='column' xs={7}>
                      <Typography>Secondary Color</Typography>
                    </Grid>

                    <Grid item container direction='column' xs={5}>
                      <Button
                        variant='contained'
                        className={classes.greenBtn}
                        onClick={toggleSecondaryColorPicker}
                      >
                        Pick Color
                      </Button>
                    </Grid>
                  </Grid>

                  <Grid item container direction='row' className='mt-4'>
                    {colorPickerDisplaySecondary &&
                      <div style={popover}>
                        <div style={cover} onClick={toggleSecondaryColorPicker} />
                        <ChromePicker color={form.secondaryColor} onChange={e => setSecondaryColor(e)} />
                      </div>}
                  </Grid>

                  <Grid item>
                    <div className='' style={{ backgroundColor: form.secondaryColor, color: form.secondaryColor, height: '6em' }}>{form.secondaryColor}</div>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container item direction='row' className='mx-auto w-100'>
          <Grid item xs={8} />
          <Grid item container xs={4} justify='center'>
            <Button
              className={classes.blueBtn}
              variant='contained'
              size='large'
              disabled={!isEnabled}
              onClick={(e) => { e.preventDefault(); submitForm('user.general', form) }}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Dialog maxWidth='xs' fullWidth open={supportKeyOpen} onClose={closeSupportKeyModal}>
        <DialogTitle>
          <Typography style={{ fontWeight: 600, color: '#808080', fontSize: '22px' }}>Current Support Key</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography style={{ fontWeight: 600, color: !accountSupportKey ? '#a7a7a7' : 'black' }}>{accountSupportKey || 'Unavailable'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' className={classes.greenBtn} onClick={closeSupportKeyModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default OrganizationGeneral
